<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['changeFilter']);

const store = useStore();
const { t } = useI18n();
const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const [showActionsDropdown, toggleDropdown] = useToggle();

const currentSortBy = computed(
  () => chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
);

// Ordered to match the Sort-by menu design.
const SORT_OPTIONS = [
  'last_activity_at_desc',
  'last_activity_at_asc',
  'created_at_desc',
  'created_at_asc',
  'priority_desc',
  'priority_asc',
  'waiting_since_asc',
  'waiting_since_desc',
];

const chatSortOptions = computed(() =>
  SORT_OPTIONS.map(value => ({
    value,
    label: t(`CHAT_LIST.SORT_ORDER_ITEMS.${value}.TEXT`),
  }))
);

const handleSortChange = value => {
  emit('changeFilter', value, 'sort');
  store.dispatch('setChatSortFilter', value);
  updateUISettings({
    conversations_filter_by: {
      status: chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN,
      order_by: value,
    },
  });
  toggleDropdown(false);
};
</script>

<template>
  <div class="relative flex">
    <button
      v-tooltip.right="$t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
      class="header-button"
      @click="toggleDropdown"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
        class="header-button-icon"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"
        />
      </svg>
    </button>
    <div
      v-if="showActionsDropdown"
      v-on-click-outside="() => toggleDropdown(false)"
      class="absolute z-40 mt-1 top-full w-64 p-1.5 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg"
      :class="{
        'ltr:left-0 rtl:right-0': !isOnExpandedLayout,
        'ltr:right-0 rtl:left-0': isOnExpandedLayout,
      }"
    >
      <div class="px-2 py-1 text-xs font-medium text-n-slate-10">
        {{ $t('CHAT_LIST.CHAT_SORT.SORT_BY') }}
      </div>
      <button
        v-for="option in chatSortOptions"
        :key="option.value"
        class="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-left rounded-md text-n-slate-12 hover:bg-n-alpha-2"
        @click="handleSortChange(option.value)"
      >
        <span
          class="grid border rounded-full place-content-center size-4 shrink-0"
          :class="
            option.value === currentSortBy ? 'border-n-brand' : 'border-n-strong'
          "
        >
          <span
            v-if="option.value === currentSortBy"
            class="rounded-full size-2 bg-n-brand"
          />
        </span>
        <span class="truncate">{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.header-button {
  color: #6f7584;
  background: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    color: #181f30;
  }
}
.header-button-icon {
  width: 16px;
  height: 16px;
  stroke-width: 1.8;
}
</style>
